import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getLocale } from 'umi';
import { v4 as uuidv4 } from 'uuid';

import type { IRawStreamChunk, IUseStreamingConversationResult, TStreamEvent } from '@dbl/core/global';
import { useStreamingConversation } from '@dbl/core/global';
import { buildTokenAwareRequest } from '@dbl/services/common';
import type { IAiAnalysisStreamingInput } from '../types';

import { cancelAiAnalysis, runAiAnalysis, type IBuildAiAnalysisInputParams } from '../runAiAnalysis';

export interface IUseAiAnalysisCardSessionParams {
  gameCode: string;
  dashboardId: string;
  queries?: {
    filters?: unknown[] | null;
    user_filter?: unknown;
    [key: string]: unknown;
  };
  formatMessage: (descriptor: { id: string }) => string;
}

export interface IUseAiAnalysisCardSessionResult {
  session: IUseStreamingConversationResult<IAiAnalysisStreamingInput>;
  runAnalysis: (params?: IBuildAiAnalysisInputParams) => Promise<void>;
  stopAnalysis: () => Promise<void>;
  /** 设置 AI 分析耗时指标回调，在 SSE 流返回 __metrics__ 帧时触发。传 null 清除。 */
  setAiMetricsCallback: (cb: ((durationMs: number) => void) | null) => void;
}

/**
 * useAiAnalysisCardSession：单张 AI 卡片自己的独立 SSE 会话。
 *
 * 适用场景：
 * - 卡片首次挂载后的自动分析；
 * - 用户在卡片上切换方案、点击刷新、停止生成；
 * - 没有发生"Apply 接管 preview 会话"时，卡片默认都使用这条独立 session。
 *
 * 设计取舍：
 * - 后端要求每次分析请求都使用新的 session_id，因此这里不再复用卡片级固定 sessionId；
 * - stop/unmount 需要取消"当前这次请求"，因此必须记录最近一次请求的 sessionId/messageId。
 */
export const useAiAnalysisCardSession = (
  params: IUseAiAnalysisCardSessionParams,
): IUseAiAnalysisCardSessionResult => {
  const { gameCode, dashboardId, queries, formatMessage } = params;
  // ===== 非渲染态上下文 =====
  // 记录每条 message 最新的 artifact.parts，用于把"整段重发"还原成文本增量。
  const artifactTextPartsByMessageIdRef = useRef<Record<string, string[]>>({});
  // AI 分析耗时指标回调：在 SSE 流返回 __metrics__ 帧时触发，由 controller 通过 runtimeApi 设置。
  const aiMetricsCallbackRef = useRef<((durationMs: number) => void) | null>(null);
  // 后端要求 session_id 每次请求都不同；这里用 ref 记录"最近一次请求"的 sessionId，供 stop/unmount cancel 使用。
  const sessionIdRef = useRef<string>(uuidv4());
  // 记录最近一次请求的 messageId，供 stop/unmount 时调用 cancelAiAnalysis 使用。
  const latestMessageIdRef = useRef<string | undefined>(undefined);
  // 用 ref 持有最新 streamingState，避免卸载清理 effect 里拿到过期闭包值。
  const latestStreamingStateRef = useRef<string>('idle');

  // sessionConfig 描述了这张卡如何发请求、如何生成 messageId、以及如何把 SSE chunk 翻译成统一事件。
  const sessionConfig = useMemo(() => ({
    buildRequest: async (
      input: IAiAnalysisStreamingInput,
      context: { messageId: string },
    ) => (
      buildTokenAwareRequest({
        url: '/api/v2/datalab/ai/chat',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        data: {
          game_code: gameCode,
          dashboard_id: dashboardId,
          queries,
          ...input,
          language: getLocale(),
          session_id: sessionIdRef.current,
          message_id: context.messageId,
          message: '',
          // 后端计算动态日期时间需要的
          request_time: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
    ),
    createMessageId: () => uuidv4(),
    onBeforeSend: ({ messageId }: { messageId: string }) => {
      // 发送前先保存 messageId，确保流刚发出但 UI 还没写入 conversation 时也能执行 stop。
      latestMessageIdRef.current = messageId;
    },
    parseChunk: (rawChunk: IRawStreamChunk, context: { messageId: string; input: IAiAnalysisStreamingInput }): TStreamEvent[] => {
      // 空帧忽略，避免污染下游会话状态。
      if (!rawChunk?.data) {
        return [];
      }
      try {
        const payload = JSON.parse(rawChunk.data);

        if (
          rawChunk.event === 'done'
          || payload?.type === 'done'
          || payload?.result?.final === true
          || payload?.result?.status?.state === 'completed'
          || payload?.id === 'completed'
        ) {
          return [{ kind: 'DONE' }];
        }
        if (rawChunk.event === 'message' && payload?.type === 'text') {
          return [{ kind: 'TEXT_DELTA', text: String(payload?.content || '') }];
        }
        if (rawChunk.event === 'message' && payload?.type === 'progress') {
          return [{ kind: 'TEXT_DELTA', text: `${String(payload?.content || '')}\n` }];
        }
        const artifactParts = payload?.result?.artifact?.parts;
        if (Array.isArray(artifactParts)) {
          // 某些返回不是 token 级流，而是 artifact.parts 的完整快照。
          // 这里通过与上一帧比较，只吐出新增文本，保证 UI 侧看到的是"流式追加"。
          const currentTextParts = artifactParts
            .filter((part) => part?.type === 'text' && typeof part?.text === 'string')
            .map((part) => part.text);
          if (currentTextParts.length) {
            const messageId = context.messageId;
            const prevTextParts = artifactTextPartsByMessageIdRef.current[messageId] || [];
            const events: TStreamEvent[] = [];
            const maxPrefixLength = Math.min(prevTextParts.length, currentTextParts.length);
            let commonPrefixCount = 0;
            // 找出两次 snapshot 共有的 parts 前缀，后续只处理变化部分。
            while (
              commonPrefixCount < maxPrefixLength
              && prevTextParts[commonPrefixCount] === currentTextParts[commonPrefixCount]
            ) {
              commonPrefixCount += 1;
            }

            if (
              commonPrefixCount < prevTextParts.length
              && commonPrefixCount < currentTextParts.length
            ) {
              const prevPart = prevTextParts[commonPrefixCount] || '';
              const currentPart = currentTextParts[commonPrefixCount] || '';
              if (currentPart.startsWith(prevPart)) {
                const partDelta = currentPart.slice(prevPart.length);
                if (partDelta) {
                  events.push({
                    kind: 'TEXT_DELTA',
                    text: partDelta,
                  });
                }
                commonPrefixCount += 1;
              }
            }

            for (let i = commonPrefixCount; i < currentTextParts.length; i += 1) {
              const text = currentTextParts[i];
              if (!text) {
                continue;
              }
              events.push({
                kind: 'TEXT_DELTA',
                text,
              });
            }

            artifactTextPartsByMessageIdRef.current[messageId] = currentTextParts;
            return events;
          }
        }

        // __metrics__ 恒在 SSE 流末尾出现，放最后判断避免 99.9% 的 chunk 做无意义比较。
        if (payload?.id === '__metrics__' && typeof payload?.result?.duration_ms === 'number') {
          aiMetricsCallbackRef.current?.(payload.result.duration_ms);
          aiMetricsCallbackRef.current = null;
          return [];
        }

        return [];
      } catch (error) {
        const chunkData = String(rawChunk?.data || '').trim();
        // 后端 SSE 可能包含非 JSON 控制帧（如 [DONE]），这些帧应忽略而不是打断渲染。
        if (!chunkData || chunkData === '[DONE]') {
          return [];
        }
        return [
          {
            kind: 'ERROR',
            error,
            message: formatMessage({ id: 'dbl_ai_analysis_stream_parse_error' }),
          },
        ];
      }
    },
  }), [dashboardId, formatMessage, gameCode, queries]);

  // session 是 useStreamingConversation 维护的真正流式状态机。
  const session = useStreamingConversation<IAiAnalysisStreamingInput>(sessionConfig);

  const { cancelStreaming, clearConversations } = session;

  // 把最新 streamingState 同步到 ref。
  // 原因：卸载清理函数只在 unmount 时执行一次，直接依赖 session.streamingState 容易拿到旧值。
  useEffect(() => {
    latestStreamingStateRef.current = session.streamingState;
  }, [session.streamingState]);

  /**
   * 卸载时的兜底清理。
   * - 如果此时还在 streaming，需要显式通知后端停止生成；
   * - 无论后端 cancel 是否成功，本地都要 cancelStreaming + clearConversations，避免内存和 UI 残留。
   */
  useEffect(() => () => {
    const isStreaming = latestStreamingStateRef.current === 'pending' || latestStreamingStateRef.current === 'progressing';
    if (isStreaming) {
      cancelAiAnalysis({
        sessionId: sessionIdRef.current,
        messageId: latestMessageIdRef.current,
      }).catch(() => {});
    }
    cancelStreaming();
    clearConversations();
  }, [cancelStreaming, clearConversations]);

  /**
   * 在当前卡片 session 上发起一次新分析。
   * `runAiAnalysis` 内部会先 clearConversations，再 sendMessage，
   * 因此这里不需要额外清空历史。
   */
  const runAnalysis = useCallback(
    async (runParams: IBuildAiAnalysisInputParams = {}) => {
      // 原因：后端要求每次请求都生成新的 session_id，否则会拒绝或无法正确串联会话。
      // 这里在 sendMessage 之前更新，确保 buildRequest 与 cancel 都指向同一次请求。
      sessionIdRef.current = uuidv4();
      await runAiAnalysis({
        session,
        ...runParams,
      });
    },
    [session],
  );

  /**
   * 手动停止当前卡片分析。
   * 与卸载清理的区别：
   * - 这里是用户显式点击 Stop；
   * - 停止后立刻清空当前卡片内容，让卡片回到"可重新发起分析"的干净状态。
   */
  const stopAnalysis = useCallback(async () => {
    cancelAiAnalysis({
      sessionId: sessionIdRef.current,
      messageId: latestMessageIdRef.current,
    }).catch(() => {});
    session.cancelStreaming();
    session.clearConversations();
    // 清除埋点回调，防止停止后回调残留，在后续分析中误上报。
    aiMetricsCallbackRef.current = null;
  }, [session]);

  const setAiMetricsCallback = useCallback((cb: ((durationMs: number) => void) | null) => {
    aiMetricsCallbackRef.current = cb;
  }, []);

  return {
    session,
    runAnalysis,
    stopAnalysis,
    setAiMetricsCallback,
  };
};

